import { useMeta } from 'quasar';
import { escapeMetaContent } from 'utils/html-util';

// Quasar writes every key of a meta or link entry into the document as an attribute, and every htmlAttr and bodyAttr
// value as one too, so each of them needs escaping for an attribute context.
const ATTRIBUTE_ENTRIES = ['meta', 'link'];
const ATTRIBUTE_MAPS = ['htmlAttr', 'bodyAttr'];

/**
 * Registers a page's head content, escaping the text Quasar writes into the served HTML on the way through.
 *
 * Quasar concatenates the title and every attribute straight into the document during a server render, and again into
 * the qmeta-init script tag, neither of which escapes anything. Escaping here rather than at each page means no page
 * has to remember, and the one rule left is that pages call this instead of useMeta.
 *
 * A title template is left alone, being a function over a title that has already come through here. A script block is
 * left alone too; it belongs to a script context rather than an attribute one and is serialized on its own terms, as
 * is the authored markup of a noscript block. An entry's own key reaches the document as its data-qmeta value and is
 * left alone as well, since escaping it on the server alone would leave the client rebuilding every tag on hydration.
 *
 * @param {Function|Object} meta - The meta object, or a factory returning one for the current render. Quasar's useMeta
 *     accepts either, and pages use both.
 */
export function usePageMeta(meta) {
    if (typeof meta === 'function') {
        useMeta(() => escapePageMeta(meta()));
        return;
    }
    useMeta(escapePageMeta(meta));
}

function escapePageMeta(meta) {
    if (!meta) {
        return meta;
    }
    const escaped = { ...meta };
    if (typeof meta.title === 'string') {
        escaped.title = escapeMetaContent(meta.title);
    }
    ATTRIBUTE_ENTRIES.filter(channel => meta[channel]).forEach(channel => {
        escaped[channel] = escapeEntries(meta[channel]);
    });
    ATTRIBUTE_MAPS.filter(channel => meta[channel]).forEach(channel => {
        escaped[channel] = escapeValues(meta[channel]);
    });
    return escaped;
}

/**
 * Escapes every entry of a channel. A page hands meta as an object keyed by name and link as an array, and Quasar
 * accepts either for both, so the shape is preserved rather than assumed.
 *
 * @param {Object|Object[]} entries - The entries the page declared for the channel.
 * @returns {Object|Object[]} - The entries, with every attribute value escaped.
 */
function escapeEntries(entries) {
    if (Array.isArray(entries)) {
        return entries.map(entry => escapeValues(entry));
    }
    return Object.fromEntries(Object.entries(entries).map(([name, entry]) => [name, escapeValues(entry)]));
}

/**
 * Escapes the string values of an attribute map, leaving anything else as it stands. A boolean attribute carries true
 * rather than text, which Quasar writes as a bare name.
 *
 * @param {Object} values - The attribute names against their values.
 * @returns {Object} - The same map, with every string value escaped.
 */
function escapeValues(values) {
    if (!values || typeof values !== 'object') {
        return values;
    }
    return Object.fromEntries(Object.entries(values).map(([name, value]) =>
        [name, typeof value === 'string' ? escapeMetaContent(value) : value]));
}
